// Canyon-style rock (owner 2026-11-01: 'make the mountains / rock structures look more like canyon style rock structures',
// 'more texture… only for looks'). Render-only: layered sandstone strata in world space (so every butte, spire, boulder and
// rock shares continuous bands), wind-eroded ledges and vertical grooves in the geometry, and crack lines. Sim footprints and
// collision are unchanged.
import * as THREE from 'three/webgpu';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { color, float, fract, mix, mx_noise_float, mx_worley_noise_vec2, normalWorld, positionWorld, smoothstep, step, vec3 } from 'three/tsl';
import { RenderStyle, toonMaterial } from './toon';

/** Sandstone strata colour node (world-space bands, noise-warped so the layers undulate; stepped for the cel look). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function strataColor(tint = 1): any {
  const p = positionWorld;
  const warp = mx_noise_float(p.xz.mul(0.035)).mul(2.2).add(mx_noise_float(p.mul(0.25)).mul(0.35));
  const y = p.y.add(warp);
  // four repeating layers ~1.6 m thick with thin dark seams between them
  const band = y.mul(0.62).fract();
  const layer = y.mul(0.62).floor().mul(0.37).fract(); // pseudo-random per layer
  const c0 = color(0xc8834f), c1 = color(0xdba673), c2 = color(0xb0603c), c3 = color(0xe7c597);
  let col = mix(c0, c1, smoothstep(0.24, 0.26, layer));
  col = mix(col, c2, smoothstep(0.49, 0.51, layer));
  col = mix(col, c3, smoothstep(0.74, 0.76, layer));
  const seam = smoothstep(0.06, 0.0, band).add(smoothstep(0.94, 1.0, band)).clamp(0, 1);
  col = mix(col, color(0x6e3a24), seam.mul(0.55));
  // grain + desert varnish streaks running down the faces
  const grain = mx_noise_float(p.mul(3.1)).mul(0.5).add(0.5);
  col = col.mul(smoothstep(0.35, 0.65, grain).mul(0.1).add(0.93));
  const streak = mx_noise_float(vec3(p.x.mul(1.4), p.y.mul(0.08), p.z.mul(1.4))).mul(0.5).add(0.5);
  col = mix(col, color(0x7a4a32), smoothstep(0.7, 0.78, streak).mul(0.35));
  // cracks: worley cell edges
  const w = mx_worley_noise_vec2(vec3(p.x.add(p.z).mul(0.45), p.y.mul(0.7), p.z.sub(p.x).mul(0.45)));
  const crack = smoothstep(0.06, 0.0, w.y.sub(w.x));
  col = mix(col, color(0x4a2618), crack.mul(0.6));
  return col.mul(float(tint));
}

/**
 * Painted anime mesa (owner 2026-11-01 reference sheet: tall flat-topped tan cliffs, vertical fluting, grass caps that
 * drape over the rim). Tan stone with vertical light/shadow flutes and dark vertical crack strokes; any upward-facing
 * surface (tops, ledges) gets a soft two-tone grass cap whose edge is noise-ragged so it drips over the rim.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mesaColor(tint = 1, grass = true): any {
  const p = positionWorld;
  const flute = mx_noise_float(vec3(p.x.mul(0.9), p.y.mul(0.07), p.z.mul(0.9))).mul(0.5).add(0.5);
  const flute2 = mx_noise_float(vec3(p.x.mul(2.6), p.y.mul(0.16), p.z.mul(2.6)).add(3.0)).mul(0.5).add(0.5);
  let col = mix(color(0xa38c68), color(0xd8c6a0), smoothstep(0.38, 0.62, flute));               // shadow flute → lit flute
  col = mix(col, color(0xe8dab8), smoothstep(0.72, 0.8, flute2).mul(0.6));                       // bright painted highlights
  // vertical crack strokes: thin wavering lines where a vertically-stretched noise crosses mid-value (painted, not tiled)
  const cn = mx_noise_float(vec3(p.x.mul(1.3), p.y.mul(0.045), p.z.mul(1.3)).add(11.0));
  col = mix(col, color(0x6a5640), smoothstep(0.06, 0.0, cn.abs()).mul(0.7));
  const cn2 = mx_noise_float(vec3(p.x.mul(0.55), p.y.mul(0.03), p.z.mul(0.55)).add(5.0));
  col = mix(col, color(0x7d6a4e), smoothstep(0.12, 0.0, cn2.abs()).mul(0.45));
  col = mix(col, color(0x8a7556), smoothstep(0.55, 0.3, normalWorld.y.add(0.6)).mul(0.25));      // undersides a touch darker
  // owner 2026-11-02 'more textures to the rocks': painted strata bands, a warm / cool colour drift, branching cracks,
  // sunlit upper edges, and (on loose rocks + boulders) moss on the tops and lichen spots
  const drift = mx_noise_float(p.mul(0.07).add(4.4)).mul(0.5).add(0.5);
  col = mix(col, col.mul(vec3(1.07, 0.98, 0.88)), smoothstep(0.55, 0.8, drift).mul(0.7));
  col = mix(col, col.mul(vec3(0.92, 0.95, 1.0)), smoothstep(0.45, 0.2, drift).mul(0.6));
  const sy = p.y.mul(0.42).add(mx_noise_float(p.mul(0.18)).mul(0.7)).add(mx_noise_float(p.xz.mul(0.05)).mul(1.5));
  const band = sy.fract(), layer = sy.floor().mul(0.618).fract();
  col = mix(col, col.mul(vec3(1.05, 1.0, 0.94)), smoothstep(0.45, 0.55, layer).mul(0.5));          // alternating layer tones
  col = mix(col, color(0x8c7151), smoothstep(0.035, 0.0, band).add(smoothstep(0.965, 1.0, band)).clamp(0, 1).mul(smoothstep(0.3, 0.6, layer)).mul(0.3)); // thin seams, every other layer or so
  const wc = mx_worley_noise_vec2(vec3(p.x.add(p.z).mul(0.6), p.y.mul(0.9), p.z.sub(p.x).mul(0.6)));
  const crack = smoothstep(0.035, 0.0, wc.y.sub(wc.x)).mul(smoothstep(0.58, 0.72, mx_noise_float(p.mul(0.4).add(9.0)).mul(0.5).add(0.5)));
  col = mix(col, color(0x5a4632), crack.mul(0.42));
  const rim = smoothstep(0.3, 0.55, normalWorld.y).mul(smoothstep(0.85, 0.62, normalWorld.y));
  col = mix(col, color(0xf2e6c6), rim.mul(0.32));                                                  // sunlit upper edges
  if (!grass) {
    const mossN = mx_noise_float(p.mul(1.3).add(2.0)).mul(0.5).add(0.5).add(mx_noise_float(p.mul(4.1)).mul(0.12));
    const moss = smoothstep(0.5, 0.7, normalWorld.y.add(mossN.sub(0.5).mul(0.5))).mul(smoothstep(0.48, 0.6, mossN));
    col = mix(col, mix(color(0x4f8a36), color(0x86b84e), smoothstep(0.5, 0.75, mossN)), moss.mul(0.9));
    const lw = mx_worley_noise_vec2(p.mul(2.2).add(3.0)), lichen = smoothstep(0.16, 0.1, lw.x).mul(smoothstep(0.6, 0.72, mx_noise_float(p.mul(0.5).add(1.0)).mul(0.5).add(0.5)));
    col = mix(col, mix(color(0xc9cf7c), color(0xd9965a), step(0.5, fract(lw.x.mul(37.0)))), lichen.mul(0.75));
  }
  if (grass) {
    const rag = mx_noise_float(p.mul(0.55)).mul(0.22).add(mx_noise_float(p.mul(2.2)).mul(0.08));
    const cap = smoothstep(0.42, 0.5, normalWorld.y.add(rag));
    const gtone = mix(color(0x3f9a3a), color(0x7fcf55), smoothstep(0.35, 0.65, mx_noise_float(p.mul(0.35)).mul(0.5).add(0.5)));
    col = mix(col, gtone, cap);
  }
  return col.mul(float(tint));
}

export function canyonMaterial(style: RenderStyle, tint = 1, grass = true): THREE.Material {
  const m = style === 'cel' ? toonMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: 0.95 });
  (m as THREE.NodeMaterial).colorNode = mesaColor(tint, grass);
  return m;
}

const hash = (a: number, b: number) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };
const vnoise = (x: number, y: number) => {
  const i = Math.floor(x), j = Math.floor(y), fx = x - i, fy = y - j, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return (hash(i, j) * (1 - u) + hash(i + 1, j) * u) * (1 - v) + (hash(i, j + 1) * (1 - u) + hash(i + 1, j + 1) * u) * v;
};

/**
 * Eroded canyon column, unit radius at the base, unit height, base at y=0. `tiers` stepped ledges (each tier tucks in a
 * little with a slightly overhanging cap), vertical wind grooves around the circumference, a flat or rounded top.
 */
export function canyonColumn(seed: number, opts: { tiers?: number; taper?: number; groove?: number; radial?: number; rows?: number; dome?: number } = {}): THREE.BufferGeometry {
  const tiers = opts.tiers ?? 4, taper = opts.taper ?? 0.35, groove = opts.groove ?? 0.09;
  const radial = opts.radial ?? 28, rows = opts.rows ?? 24, dome = opts.dome ?? 0;
  const g = new THREE.CylinderGeometry(1, 1, 1, radial, rows, false).translate(0, 0.5, 0);
  const pos = g.attributes.position as THREE.BufferAttribute; const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const y = v.y, ang = Math.atan2(v.z, v.x), rr = Math.hypot(v.x, v.z);
    if (rr < 1e-4) { if (y > 0.99) pos.setY(i, 1 + dome * 0.15); continue; } // cap centre
    const tierF = y * tiers, ti = Math.min(tiers - 1, Math.floor(tierF)), f = tierF - ti;
    // each tier: slightly narrower than the one below; its top lip overhangs (sandstone over softer shale)
    let r = 1 - taper * (ti / tiers) - taper * 0.25 * f;
    r += f > 0.82 ? 0.05 * Math.sin(((f - 0.82) / 0.18) * Math.PI) : 0;
    r *= 1 - groove * vnoise(ang * 3.2 + seed * 7, y * 2 + seed) - groove * 0.6 * Math.abs(Math.sin(ang * (5 + (seed % 3)) + seed));
    r *= 0.92 + 0.16 * vnoise(ang * 1.3 + seed * 3, 0.5 + seed);
    const k = rr === 0 ? 0 : r / rr;
    v.x *= k; v.z *= k;
    if (y > 0.99 && dome > 0) v.y = 1 + dome * 0.15 * (1 - rr);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4); w.computeVertexNormals(); return w;
}

/** Weathered boulder / rock: smooth high-poly blob with flattened bedding planes (reads as broken sandstone). */
export function canyonBoulder(seed: number, detail = 3): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, detail);
  g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4);
  const pos = w.attributes.position as THREE.BufferAttribute; const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    let s = 0.82 + 0.26 * vnoise(v.x * 1.8 + seed, v.z * 1.8 + v.y * 1.3) + 0.08 * vnoise(v.x * 5 + seed * 2, v.y * 5 - v.z * 3);
    // bedding planes: terrace the height slightly
    const yq = Math.round(v.y * 3) / 3; v.y = v.y * 0.65 + yq * 0.35;
    if (v.y < -0.35) v.y = -0.35 - (v.y + 0.35) * 0.2; // flat-ish underside sitting in the ground
    v.multiplyScalar(s); s = 0;
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  w.computeVertexNormals(); return w;
}
